import type { JoinSplitNote, StoredJoinSplitNote } from "../joinsplit/types";
import type { FlatJoinSplitV2NoteSyncResult } from "./sync";

/** Local per-wallet index that lets a refresh apply only what changed since the
 * last sync: cursors into the public chain feed and the owner mailbox lanes,
 * the leaf position and nullifier of every note this wallet holds, and the
 * commitments it has seen spent. Only this wallet's own entries are stored;
 * the feeds themselves are global and filtered locally. */
export interface SyncChainCursor {
  tree: number;
  leaf: number;
  block: number;
  log: number;
}

export interface SyncIndexState {
  version: 2;
  /** Chain index generation the cursors were taken from; a change means the
   * server rebuilt its index and every cursor must be discarded. */
  generation: number;
  chain: SyncChainCursor;
  lanes: { pending: number; protected: number };
  legacy: { records: number; nextPosition: number; spentMarkers: number };
  positions: Record<string, { treeIndex: number; leafIndex: number }>;
  nullifiers: Record<string, string>;
  spent: string[];
  fullSyncAt: number;
  /** Relayer fee-recovery cursor per pool at the last sync; equal positions
   * let the incremental path skip the recovery feed entirely. */
  relayer?: Record<string, number>;
  /** Fingerprint (syncHead.ts) of the sync head the index was committed at. */
  headFingerprint?: string;
}

export interface SyncChainLeaf {
  commitment: string;
  treeIndex: number;
  leafIndex: number;
}

export interface SyncChainNullifier {
  nullifier: string;
  blockNumber: number;
  logIndex: number;
}

export interface SyncChainPage {
  leaves: SyncChainLeaf[];
  nullifiers: SyncChainNullifier[];
  next: SyncChainCursor;
  more: { leaves: boolean; nullifiers: boolean };
}

export interface SyncChainDelta {
  leaves: SyncChainLeaf[];
  nullifiers: SyncChainNullifier[];
  next: SyncChainCursor;
}

/** The compact head reading the refresh already fetched (see syncHead.ts). */
export interface SyncHeadCursors {
  mailbox: {
    pending: { records: number; nextPosition: number };
    protected: { records: number; nextPosition: number };
    legacy: { records: number; nextPosition: number; spentMarkers: number };
  };
  chain: {
    generation: number;
    currentTree: number;
    nextLeaf: number;
    lastEvent: { blockNumber: number; logIndex: number } | null;
  } | null;
}

/** A full chain validation is repeated at least this often even when every
 * delta applied cleanly, so the local index never drifts from the chain. */
export const FULL_SYNC_MAX_AGE_MS = 24 * 60 * 60 * 1000;
export const MAX_DELTA_PAGES = 40;

const lower = (value: string) => value.toLowerCase();
const finite = (value: unknown): value is number =>
  typeof value === "number" && Number.isSafeInteger(value) && value >= 0;

export function emptySyncIndex(): SyncIndexState {
  return {
    version: 2,
    generation: -1,
    chain: { tree: 0, leaf: 0, block: 0, log: -1 },
    lanes: { pending: 0, protected: 0 },
    legacy: { records: 0, nextPosition: 0, spentMarkers: 0 },
    positions: {},
    nullifiers: {},
    spent: [],
    fullSyncAt: 0,
  };
}

export function isSyncIndexState(value: unknown): value is SyncIndexState {
  const v = value as SyncIndexState | null;
  return (
    !!v &&
    typeof v === "object" &&
    v.version === 2 &&
    Number.isSafeInteger(v.generation) &&
    !!v.chain &&
    finite(v.chain.tree) &&
    finite(v.chain.leaf) &&
    finite(v.chain.block) &&
    Number.isSafeInteger(v.chain.log) &&
    v.chain.log >= -1 &&
    !!v.lanes &&
    finite(v.lanes.pending) &&
    finite(v.lanes.protected) &&
    !!v.legacy &&
    finite(v.legacy.records) &&
    finite(v.legacy.nextPosition) &&
    finite(v.legacy.spentMarkers) &&
    !!v.positions &&
    typeof v.positions === "object" &&
    !!v.nullifiers &&
    typeof v.nullifiers === "object" &&
    Array.isArray(v.spent) &&
    finite(v.fullSyncAt) &&
    (v.headFingerprint === undefined || typeof v.headFingerprint === "string")
  );
}

/** The index was committed at exactly the head with this fingerprint. */
export function syncIndexVouchesForHead(
  index: SyncIndexState | null,
  headFingerprint: string,
): boolean {
  return !!index?.headFingerprint && index.headFingerprint === headFingerprint;
}

/** After an unchanged sync head, the local notes that still need the chain.
 * Null when the index cannot vouch for that head (missing, or committed at
 * another one): the caller then validates every note. Otherwise only notes the
 * index neither placed nor marked spent, and notes this device has not placed
 * yet: an unchanged head proves no leaf was inserted and no nullifier used
 * since the index was committed, so every note it accounts for keeps its state. */
export function notesUnknownAtUnchangedHead<
  T extends { note: { commitment: string; leafIndex: number } },
>(params: {
  index: SyncIndexState | null;
  headFingerprint: string;
  notes: readonly T[];
}): T[] | null {
  const { index } = params;
  if (!index || !syncIndexVouchesForHead(index, params.headFingerprint))
    return null;
  const known = new Set(
    [...Object.keys(index.positions), ...index.spent].map(lower),
  );
  return params.notes.filter(
    (stored) =>
      typeof stored.note.commitment !== "string" ||
      !known.has(lower(stored.note.commitment)) ||
      !finite(stored.note.leafIndex),
  );
}

/** Cursors for a head observed *before* a full validation started: anything
 * that lands after the reading moves a cursor, so the next delta includes it. */
export function syncIndexCursorsFromHead(
  head: SyncHeadCursors,
): Pick<SyncIndexState, "generation" | "chain" | "lanes" | "legacy"> {
  if (!head.chain) throw new Error("Chain index head is unavailable");
  return {
    generation: head.chain.generation,
    chain: {
      tree: head.chain.currentTree,
      leaf: head.chain.nextLeaf,
      block: head.chain.lastEvent?.blockNumber ?? 0,
      log: head.chain.lastEvent?.logIndex ?? -1,
    },
    lanes: {
      pending: head.mailbox.pending.nextPosition,
      protected: head.mailbox.protected.nextPosition,
    },
    legacy: { ...head.mailbox.legacy },
  };
}

/** A delta may replace the full validation only when the index was seeded by a
 * recent full validation of the same server generation, the head is
 * authoritative, and no record entered the legacy inbox (which has no
 * cursor). Spent markers don't count: they only hide inbox copies, the delta
 * takes spent state from chain nullifiers, and refreshes write them. */
export function canApplyDelta(params: {
  index: SyncIndexState | null;
  head: SyncHeadCursors;
  authoritative: boolean;
  now?: number;
}): { ok: boolean; reason?: string } {
  const { index, head } = params;
  const now = params.now ?? Date.now();
  if (!index) return { ok: false, reason: "no local index" };
  if (!params.authoritative || !head.chain)
    return { ok: false, reason: "chain index not verified" };
  if (index.generation !== head.chain.generation)
    return { ok: false, reason: "chain index rebuilt" };
  if (!index.fullSyncAt || now - index.fullSyncAt > FULL_SYNC_MAX_AGE_MS)
    return { ok: false, reason: "full validation due" };
  const legacy = head.mailbox.legacy;
  if (
    legacy.records !== index.legacy.records ||
    legacy.nextPosition !== index.legacy.nextPosition
  )
    return { ok: false, reason: "legacy inbox changed" };
  return { ok: true };
}

/** Pages the public chain feed from the cursor until neither stream has more. */
export async function fetchChainDelta(params: {
  cursor: SyncChainCursor;
  fetchPage: (cursor: SyncChainCursor) => Promise<SyncChainPage>;
  maxPages?: number;
}): Promise<SyncChainDelta> {
  const maxPages = params.maxPages ?? MAX_DELTA_PAGES;
  const leaves: SyncChainLeaf[] = [];
  const nullifiers: SyncChainNullifier[] = [];
  let cursor = { ...params.cursor };
  for (let page = 0; ; page++) {
    if (page >= maxPages) throw new Error("Chain delta exceeds the page limit");
    const result = await params.fetchPage(cursor);
    for (const leaf of result.leaves) {
      if (
        typeof leaf.commitment !== "string" ||
        !/^0x[0-9a-f]{64}$/.test(leaf.commitment) ||
        !finite(leaf.treeIndex) ||
        !finite(leaf.leafIndex)
      )
        throw new Error("Malformed chain delta leaf");
      leaves.push(leaf);
    }
    for (const entry of result.nullifiers) {
      if (
        typeof entry.nullifier !== "string" ||
        !/^0x[0-9a-f]{64}$/.test(entry.nullifier) ||
        !finite(entry.blockNumber) ||
        !finite(entry.logIndex)
      )
        throw new Error("Malformed chain delta nullifier");
      nullifiers.push(entry);
    }
    const next = result.next;
    if (
      !next ||
      !finite(next.tree) ||
      !finite(next.leaf) ||
      !finite(next.block) ||
      !Number.isSafeInteger(next.log) ||
      next.log < -1 ||
      next.tree < cursor.tree ||
      (next.tree === cursor.tree && next.leaf < cursor.leaf) ||
      next.block < cursor.block ||
      (next.block === cursor.block && next.log < cursor.log)
    )
      throw new Error("Chain delta cursor moved backwards");
    const progressed =
      next.tree !== cursor.tree ||
      next.leaf !== cursor.leaf ||
      next.block !== cursor.block ||
      next.log !== cursor.log;
    cursor = next;
    if (!result.more.leaves && !result.more.nullifiers) break;
    if (!progressed) throw new Error("Chain delta did not advance");
  }
  return { leaves, nullifiers, next: cursor };
}

export interface IndexedSyncInput {
  index: SyncIndexState;
  candidates: readonly StoredJoinSplitNote[];
  delta: SyncChainDelta;
  head: SyncHeadCursors;
  safeBlock: number;
  safeBlockHash: string;
  chainId: number;
  /** Pool token hash for (pool, tree, token); cached by the caller. */
  tokenHashFor: (
    pool: string,
    treeIndex: number,
    tokenAddress: string,
  ) => Promise<string>;
  authenticate: (params: {
    note: JoinSplitNote;
    poolAddress: string;
    tokenAddress: string;
    expectedTokenHash: string;
  }) => Promise<void>;
  nullifierFor: (tokenHash: bigint, leafIndex: number) => Promise<string>;
  treeKeyFor: (pool: string, treeIndex: number) => string;
  knownPools: readonly string[];
}

/** Classifies every candidate from the local index plus the delta, with no
 * chain reads: located notes are authenticated exactly like the full path,
 * spent status comes from the nullifier stream, and anything the delta cannot
 * place stays pending for the local Merkle lookup or the next full validation. */
export async function applyIndexedSync(
  input: IndexedSyncInput,
): Promise<{ result: FlatJoinSplitV2NoteSyncResult; index: SyncIndexState }> {
  const { index, delta, head } = input;
  if (!head.chain || index.generation !== head.chain.generation)
    throw new Error("Chain index rebuilt during sync");
  const leafByCommitment = new Map<string, SyncChainLeaf>();
  for (const leaf of delta.leaves)
    leafByCommitment.set(lower(leaf.commitment), leaf);
  const seenNullifiers = new Set(
    delta.nullifiers.map((n) => lower(n.nullifier)),
  );
  const positions = { ...index.positions };
  const nullifiers = { ...index.nullifiers };
  const spentSet = new Set(index.spent.map(lower));
  const validated = new Map<string, StoredJoinSplitNote>();
  const spent = new Set<string>();
  const pending = new Set<string>();
  const rejected = new Map<string, string>();
  const pools = new Set(input.knownPools.map(lower));
  const byCommitment = new Map<string, StoredJoinSplitNote[]>();
  for (const stored of input.candidates) {
    const key = lower(String(stored?.note?.commitment || ""));
    if (!/^0x[0-9a-f]{64}$/.test(key)) continue;
    const group = byCommitment.get(key) || [];
    group.push(stored);
    byCommitment.set(key, group);
  }
  for (const [key, group] of byCommitment) {
    if (spentSet.has(key)) {
      spent.add(key);
      continue;
    }
    const errors: string[] = [];
    let settled = false;
    for (const stored of group) {
      const note = stored.note;
      const pool = lower(String(note.poolAddress || ""));
      if (!pools.has(pool)) {
        errors.push("note belongs to an unknown pool");
        continue;
      }
      // Trusted positions: earlier canonical validation or this delta. Inbox
      // metadata is never trusted for a location.
      const located =
        positions[key] ??
        (() => {
          const leaf = leafByCommitment.get(key);
          if (!leaf) return undefined;
          if (note.treeIndex !== undefined && note.treeIndex !== leaf.treeIndex)
            return undefined;
          return { treeIndex: leaf.treeIndex, leafIndex: leaf.leafIndex };
        })();
      if (!located) continue;
      const tokenAddress = note.tokenData?.tokenAddress;
      if (typeof tokenAddress !== "string" || !tokenAddress) {
        errors.push("note token address is missing");
        continue;
      }
      let expectedTokenHash: string;
      try {
        expectedTokenHash = await input.tokenHashFor(
          pool,
          located.treeIndex,
          tokenAddress,
        );
      } catch (error) {
        throw error instanceof Error ? error : new Error(String(error));
      }
      const authoritative: JoinSplitNote = {
        ...note,
        commitment: key,
        leafIndex: located.leafIndex,
        treeIndex: located.treeIndex,
        treeKey: input.treeKeyFor(pool, located.treeIndex),
      };
      try {
        await input.authenticate({
          note: authoritative,
          poolAddress: pool,
          tokenAddress,
          expectedTokenHash,
        });
      } catch (error) {
        errors.push(error instanceof Error ? error.message : String(error));
        continue;
      }
      const nullifier =
        nullifiers[key] ??
        lower(
          await input.nullifierFor(authoritative.tokenHash, located.leafIndex),
        );
      positions[key] = located;
      nullifiers[key] = nullifier;
      if (seenNullifiers.has(nullifier)) {
        spentSet.add(key);
        spent.add(key);
      } else {
        validated.set(key, { ...stored, note: authoritative, spent: false });
      }
      settled = true;
      break;
    }
    if (settled) continue;
    if (errors.length && errors.length === group.length) {
      rejected.set(key, [...new Set(errors)].join("; "));
    } else {
      pending.add(key);
    }
  }
  const cursors = syncIndexCursorsFromHead(head);
  return {
    result: {
      validated,
      spent,
      pending,
      rejected,
      safeBlock: input.safeBlock,
      safeBlockHash: input.safeBlockHash,
    },
    index: {
      ...index,
      generation: cursors.generation,
      chain: delta.next,
      lanes: cursors.lanes,
      legacy: cursors.legacy,
      positions,
      nullifiers,
      spent: [...spentSet],
    },
  };
}

/** Seeds or refreshes the index from a completed full validation: every
 * validated note keeps its canonical position; spent notes are remembered so
 * later deltas never resurrect them. Cursors come from the head read before
 * the validation started. */
export function syncIndexAfterFullValidation(params: {
  previous: SyncIndexState | null;
  result: FlatJoinSplitV2NoteSyncResult;
  head: SyncHeadCursors;
  nullifiers: Record<string, string>;
  now?: number;
}): SyncIndexState {
  const cursors = syncIndexCursorsFromHead(params.head);
  const positions: SyncIndexState["positions"] = {};
  for (const [key, stored] of params.result.validated) {
    if (
      finite(stored.note.leafIndex) &&
      stored.note.treeIndex !== undefined &&
      finite(stored.note.treeIndex)
    )
      positions[lower(key)] = {
        treeIndex: stored.note.treeIndex,
        leafIndex: stored.note.leafIndex,
      };
  }
  // A full validation replaces the old canonical view, including after a reorg.
  // Remembering a previously spent note here would override a fresh unspent result.
  const spent = new Set<string>();
  for (const key of params.result.spent) spent.add(lower(key));
  const nullifiers: SyncIndexState["nullifiers"] = {};
  for (const [key, value] of Object.entries(params.nullifiers))
    nullifiers[lower(key)] = lower(value);
  return {
    version: 2,
    ...cursors,
    positions,
    nullifiers,
    spent: [...spent],
    fullSyncAt: params.now ?? Date.now(),
  };
}
